import { Paper, Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TextField, InputAdornment, Typography, Link } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandTable from '../../../components/layout/Switch/ExpandTable';
import RequestRow from '../RequestRow/RequestRow';
import SearchIcon from '@mui/icons-material/Search';
import { addSourceActiviy, resetSimulateEquivalency } from '../requestSlice';
import { useDispatch } from 'react-redux';
import EmptyMessage from '../../../components/Alerts/EmptyMessage';
import Activity from '../../../models/Activity';
import { StudentRecordActivitiy } from '../../../models/StudentRecord';
import { normalizeString } from '../../../shared/utils/normalizeString';
import { popSnackbar } from '../../SnackbarProvider/snackbarSlice';
import NewOptionDialog from '../../../components/NewOptionDialog/NewOptionDialog';
import { NewOptionDialogProps } from '../../../components/NewOptionDialog/NewOptionDialogProps';
import { v4 as uuidv4 } from 'uuid';

interface SourceActivitiesTableProps {
  optionsSourceActivities: Activity[],
  approvedActivities: StudentRecordActivitiy[] | null,
  sourceActivitiesNew: Activity[] | null,
  onChange: (event: Activity[]) => void,
  itemSelected: Activity[],
  addSourceActiviyNew: (value: Activity) => void,
  showAddActivity: boolean
  isSourceInstitutionEqualToTargetInstitution: boolean | null
  validUser: boolean
}

const SourceActivitiesTable: React.FC<SourceActivitiesTableProps> = ({ optionsSourceActivities, approvedActivities, sourceActivitiesNew, onChange, itemSelected, addSourceActiviyNew, showAddActivity, isSourceInstitutionEqualToTargetInstitution, validUser }) => {
  const [expandTable, setExpandTable] = useState(false);
  const [criteria, setCriteria] = useState('');
  const [filteredActivities, setFilteredActivities] = useState(optionsSourceActivities.length === 0 ? sourceActivitiesNew : optionsSourceActivities);
  const [openDialogActivity, setOpenDialogActivity] = useState(false);
  const [newActivityName, setNewActivityName] = useState<string | null>(null);
  const [activityCoincidenceList, setActivityCoincidenceList] = useState<Activity[]>([]);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isRowSelected = (row: Activity): boolean => {
    return itemSelected.some((selected) => selected.id === row.id);
  };

  const selectRow = (e: ChangeEvent<HTMLInputElement>, row: Activity) => {
    dispatch(resetSimulateEquivalency()); // This is for reset the simulation when the user select a new activity
    if (e.target.checked) {
      onChange([...itemSelected, row]);
    } else {
      const filteredList = itemSelected.filter((selected) => selected.id !== row.id);
      onChange(filteredList);
    }
  };

  useEffect(() => {
    // preselect approved activities
    if (approvedActivities && itemSelected?.length === 0 && optionsSourceActivities && optionsSourceActivities.length > 0 && validUser) {
      const actividadesSeleccionadas = optionsSourceActivities.filter((actividad: Activity) =>
        approvedActivities.some((item: StudentRecordActivitiy) => item.activity.id === actividad.id)
      );
      if (actividadesSeleccionadas) onChange(actividadesSeleccionadas);
    }
  }, [approvedActivities, optionsSourceActivities, validUser]);

  useEffect(() => {
    if (optionsSourceActivities) {
      setFilteredActivities((sourceActivitiesNew && sourceActivitiesNew?.length !== 0) ? sourceActivitiesNew.concat(optionsSourceActivities.filter(activity => !activity.new)) : optionsSourceActivities);
    }
  }, [optionsSourceActivities]);

  useEffect(() => {
    if (criteria === '') {
      setFilteredActivities((sourceActivitiesNew && sourceActivitiesNew?.length !== 0) ? sourceActivitiesNew.concat(optionsSourceActivities.filter(activity => !activity.new)) : optionsSourceActivities);
    } else {
      setFilteredActivities(filterByName(criteria));
    }
  }, [criteria]);

  const filterByName = (name: string) => {
    const listforFilter = (sourceActivitiesNew && sourceActivitiesNew?.length !== 0) ? sourceActivitiesNew.concat(optionsSourceActivities.filter(activity => !activity.new)) : optionsSourceActivities;
    return listforFilter.filter(activity => {
      const res = activity.name.toUpperCase().indexOf(name.toUpperCase()) > -1;
      return res;
    });
  };

  const changeCriteria = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setCriteria(name);
  };

  const handleActivity = (activity: Activity) => {
    if (activity.new && !sourceActivitiesNew?.find((item) => item.id === activity.id)) {
      // If the activity is new and already exists in sourceActivitiesNew, we just add it to the selected items
      dispatch(addSourceActiviy(activity));
      addSourceActiviyNew(activity);
    }
    if (!itemSelected.some((item) => item.id === activity.id)) {
      onChange([
        activity,
        ...itemSelected
      ]);
    }
    setCriteria('');
  };

  useEffect(() => {
    if (optionsSourceActivities?.length < 1 && sourceActivitiesNew?.length === 0) {
      onChange([]);
    }
  }, [optionsSourceActivities, sourceActivitiesNew])


  const handleOpenDialogSyllabus = () => {
    setOpenDialogActivity(true)
    setActivityCoincidenceList([])
  }

  const ActivityNewOptionDialogProps: NewOptionDialogProps = {
    onHandleCancel: () => {
      setOpenDialogActivity(false);
      setActivityCoincidenceList([])
    },
    onChangeName: (value: string) => {
      void (async () => {
        const newActivityName = value;
        if (!newActivityName.trim()) return;
        const nameToSearch = newActivityName.trim();

        let items: Activity[] = [];
        if (sourceActivitiesNew && sourceActivitiesNew.length > 0) {
          items = sourceActivitiesNew.concat(
            optionsSourceActivities.filter((activity) => !activity.new)
          );
        } else {
          items = optionsSourceActivities;
        }

        items = items?.filter((activity) =>
          activity.name.toUpperCase().includes(nameToSearch.toUpperCase()) ||
          normalizeString(activity.name) === normalizeString(nameToSearch)
        );

        if (items.length === 0) {
          setActivityCoincidenceList([]);
        } else {
          setActivityCoincidenceList(items);
        }
      })()
    },
    onHandleSave: () => {
      const sameName = activityCoincidenceList.filter((item) => {
        const normalizedItemName = normalizeString(item.name);
        const normalizedInputValue = normalizeString(newActivityName || '');
        return normalizedItemName === normalizedInputValue
      })

      if (sameName.length > 0) {
        dispatch(popSnackbar({
          type: 'error',
          message: t('newActivityErrorMessage'),
          duration: 5000
        }));
      } else if (newActivityName) {
        const newOption = {
          id: uuidv4(),
          name: newActivityName.toUpperCase(),
          new: true
        };
        handleActivity(newOption as Activity);
        setOpenDialogActivity(false);
      }
    },
    setNewName: setNewActivityName,
    newName: newActivityName,
    setCoincidentValue: (cValue: Activity) => {
      handleActivity(cValue)
      setOpenDialogActivity(false);
    },
    coincidenceList: activityCoincidenceList,
    maxLength: 200,
    uppercase: true,
    openDialog: openDialogActivity,
    setOpenDialog: setOpenDialogActivity,
    title: t('createNewActivity'),
    label: t('nameNewActivity'),
    testId: "new-activity-"
  }

  return (
    <div className="REQXXI-Paper" >
      <div className='REQXXI-Block-Container' style={{ height: '100%' }}>
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="body1" color="initial">
              {validUser && t('labelStudentRecordActivitiesTable')}
              {!validUser && showAddActivity && t('labelSourceActivitiesTable')}
              {!validUser && !showAddActivity && t('labelTargetInstAndSyllabusInstEqual')}
            </Typography>
          </Box>
        </Box>
        <Paper variant="outlined" sx={{ overflowY: 'auto' }} >
          <TableContainer sx={{ height: showAddActivity && !expandTable ? '300px' : '100%' }}>
            <Table stickyHeader className="REQXXI-Request-Table" size="small" aria-label="customized table">
              <TableHead>
                <TableRow>
                  <TableCell className="REQXXI-Request-ActivitiesSource" align="left" colSpan={3}>
                    <TextField
                      autoFocus
                      data-testid="filter-activity-input"
                      size="small"
                      variant="standard"
                      sx={{ width: '100%', p: '0' }}
                      margin="dense"
                      placeholder={t('filterMessageActivity')}
                      value={criteria}
                      onChange={changeCriteria}
                      type="text"
                      inputProps={{ 'data-testid': 'filterTextField' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        )
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody className="REQXXI-Request-TableBody" style={{ overflow: 'auto' }}>
                {filteredActivities && [...filteredActivities].sort((a, b) => Number(isRowSelected(b)) - Number(isRowSelected(a))).map((row, i) => (
                  <RequestRow
                    key={row ? row.id : i}
                    row={row}
                    isRowSelected={isRowSelected}
                    selectRow={selectRow}
                    isSourceInstitutionEqualToTargetInstitution={isSourceInstitutionEqualToTargetInstitution}
                    validUser={validUser}
                  />
                ))}
              </TableBody>
            </Table>
            {filteredActivities && filteredActivities?.length < 1 &&
              <Box sx={{ height: '230px', display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                <EmptyMessage />
              </Box>}
          </TableContainer >
        </Paper>
        {showAddActivity && filteredActivities && filteredActivities?.length > 6 &&
          <ExpandTable onToggle={setExpandTable} active={expandTable} />
        }
        {showAddActivity &&
          <div style={{ marginTop: '10px' }}>
            <Typography variant='body1' sx={{ fontWeight: '400', paddingLeft: '5px' }}>
              {t('newActivityText')}<Link data-testid="buttonAddHere" component="button" onClick={handleOpenDialogSyllabus}>{t('here')}</Link>
            </Typography>
            {openDialogActivity && <NewOptionDialog dataTestId='dialogNewActivity' {...ActivityNewOptionDialogProps} />}
            <Typography color="#949494" sx={{ fontSize: '12px', paddingLeft: '5px' }}>
              {t('newActivityHelper')}
            </Typography>
          </div>
        }
      </div>
    </div>
  );
};

export default SourceActivitiesTable;
